// The inline dimension editor: a dimension, a face figure, a wall's or inner
// wall's figure, or a corner radius retyped in place, and the typed value applied
// (a feature resized or moved to it, a shape's edge, a relation's value).
// docs/claude/dimensions.md.
import {
  type DimAnchor,
  type DimensionLine,
  type GlassSegment,
  type Polygon,
  type ProductInstance,
  type ShapeRelation,
  type Vec2,
  type WallSide,
} from '../../shared/types';
import {
  flashStatus,
  instLocked,
  invalidate,
  pushHistory,
  renderProperties,
  state,
  vp,
} from './core';
import { wallThicknessOf } from '../dent';
import {
  clampDoor,
  doorParts,
  mainRoomDoor,
} from '../door';
import {
  distance,
  distToSegment,
  instanceToWorld,
  polyBenchLength,
  toInstanceLocal,
} from '../geometry';
import {
  clampGlassToDoor,
  clampToWall,
} from '../glass';
import { tr } from '../i18n';
import {
  benchesFollowPartitions,
  DOOR_MIN,
  GLASS_MIN,
  innerFaceMitres,
  innerWallDimSplits,
  innerWallThickness,
  isPolyRoom,
  lockWallSpan,
  maxCornerRadius,
  moveWallSpanJoint,
  partitionFaces,
  polyEdges,
  polyInnerWallMetrics,
  roomCorners,
  segToWall,
  setWallLengthPin,
  solveWall,
  unlockWallSpan,
  wallChain,
  wallLengthPin,
  wallRuns,
  wallSpanJoints,
  wallSpansMinLen,
  wallView,
  writeWallChain,
} from '../polyRoom';
import {
  cornerRadiusLeader,
  DIM_TAG_WALL_OVERALL,
  dimLabelProbeAt,
  dimLabelTagAt,
  instanceCorners,
} from '../render';
import {
  setWallSegPin,
  wallBand,
  wallSegPin,
} from '../wallTool';
import {
  $,
  faceDimEdgeAt,
  hideCtxMenu,
  innerWallsOf,
  planHits,
  polyRoomEditInst,
  regenerateSauna,
  resizablePoly,
  resizeInstance,
  screenPos,
  selectedInstance,
  syncDimensions,
} from '../main';
import { recenterPolyRoom } from './vertexEdit';
import { parseMeasure } from './panelRows';
import {
  addPolyRelation,
  addRoomRelation,
  gcsDistanceResize,
  gcsSolveRing,
  polyRingCons,
  removePolyRelation,
  removeRoomRelation,
  resizeOpenPolylineSegment,
  resizePolyEdge,
  resizePolyShapeEdge,
  resizePolyWall,
  resizeWallEdge,
  type RingCons,
  ringHasLocks,
  roomRingCons,
  updateEdgeLenArray,
} from './ringConstraints';
import {
  objConstraints,
  setConstraintValue,
} from './constraintUi';
import {
  applySupportFigure,
  type SupportFigureEdit,
} from './benchSupportTool';
import {
  applySpotFigure,
  type FigureEdit,
} from './lightTools';
import {
  type InlineEditor,
  inlineEditor,
} from './inlineText';
import { unreachable } from '../../shared/exhaustive';
import {
  followVertices,
  instanceSnapPoints,
} from '../anchors';

/** What the inline figure editor is retyping: ONE value, a kind per figure, so a
 *  new kind of figure does not compile until it says how a typed value applies
 *  (`applyFigure`) and whether it carries a lock (`figureLock`). */
export type DimEdit =
  | { kind: 'constraint'; id: string }
  | { kind: 'corner'; instId: string; vertex: number }
  | { kind: 'dimension'; dimId: string }
  | { kind: 'face'; target: FaceDimTarget; edge: number; inside: boolean }
  | { kind: 'innerWall'; instId: string; wallId: string; part: 'before' | 'after' | 'thickness' }
  | { kind: 'spot'; edit: FigureEdit & { len: number } }
  | { kind: 'support'; edit: SupportFigureEdit }
  | { kind: 'wall'; instId: string; edge: number; index: number };

// A selected shape whose temporary face dimensions can be double-clicked to
// retype an edge length. One abstraction covers drawn polygons, poly-rooms and
// rectangular-footprint objects: the world-space ring the dimensions are drawn
// on, plus how to read and resize each edge — so all three behave identically.
export interface FaceDimTarget {
  ring: Vec2[];                                   // edge i = ring[i] → ring[i+1]
  ellipse: boolean;                               // ellipses have no editable edges
  length: (edge: number) => number;              // current edge length (mm)
  apply: (edge: number, newLen: number) => void; // resize + side effects + repaint
  bothSides?: boolean;                            // also editable from the inside (room interior dims)
  innerRing?: Vec2[];                             // wall-inset ring, same edge indexing as `ring`;
                                                  // present when interior clear-span dims are shown
  closed?: boolean;                               // false for open polylines (walls/lines, node benches):
                                                  // the wrap edge n-1→0 is not drawn and not editable
  isLocked?: (edge: number) => boolean;           // this edge's length is pinned (present only where a lock exists)
  toggleLock?: (edge: number, on: boolean) => void; // pin/unpin the edge's length
}
/** The selected shape's face-dimension target, or null when nothing editable is
 *  selected. Mirrors the geometry drawRoomDimensions / drawLineDimensions use. */
export function faceDimTarget(): FaceDimTarget | null {
  const poly = resizablePoly();
  if (poly) {
    const pts = poly.points;
    return {
      ring: pts,
      ellipse: !!poly.ellipse,
      length: (e) => distance(pts[e], pts[(e + 1) % pts.length]),
      // Typing a length on a face dimension also locks that edge's length, so
      // the constraint solver holds it while other edges are edited.
      apply: (e, l) => { if (resizePolyShapeEdge(poly, e, (e + 1) % pts.length, l)) { poly.edgeLengths = updateEdgeLenArray(poly.edgeLengths, poly.points.length, e, Math.round(l)); renderProperties(); invalidate(); } },
      isLocked: (e) => (poly.edgeLengths?.[e] ?? null) != null,
      toggleLock: (e, on) => {
        pushHistory();
        poly.edgeLengths = updateEdgeLenArray(poly.edgeLengths, poly.points.length, e, on ? Math.round(distance(pts[e], pts[(e + 1) % pts.length])) : null);
        renderProperties(); invalidate();
      },
    };
  }
  // A drawn wall / free line (open polyline): each segment resizes by stretching
  // the tail, the same rule its committed dimensions already use.
  const wall = state.selectedWallId ? (state.drawing.walls || []).find((w) => w.id === state.selectedWallId) : null;
  if (wall && wall.points.length >= 2) {
    const pts = wall.points;
    const done = (): void => { syncDimensions(); renderProperties(); invalidate(); };
    // A typed figure pins its segment (`segLengths`), as a polygon's edge and a
    // room's wall are: a vertex drag then holds it. The stretch moves the tail
    // rigidly, so every other segment — pinned or not — keeps its length.
    const pinAfter = (s: number): void => setWallSegPin(wall, s, distance(pts[s], pts[s + 1]));
    const togglePin = (s: number, on: boolean): void => {
      if (s < 0) return;
      pushHistory();
      setWallSegPin(wall, s, on ? distance(pts[s], pts[s + 1]) : null);
      renderProperties(); invalidate();
    };
    if (wall.style === 'line') {
      return {
        ring: pts,
        ellipse: false,
        closed: false,
        length: (e) => distance(pts[e], pts[e + 1]),
        apply: (e, l) => { if (resizeWallEdge(wall, e, e + 1, l)) { pinAfter(e); done(); } },
        isLocked: (e) => wallSegPin(wall, e) != null,
        toggleLock: togglePin,
      };
    }
    // A wall's figures are drawn on the two FACES of its band (drawWall), not on
    // its centre line, so the edit must read and write the face that was clicked:
    // with the line on one face (left / right alignment) the other face is a whole
    // thickness away, and at a corner its run is longer or shorter than the line.
    // The band is [...leftFace, ...rightFace.reversed()]: edge e < n-1 is left
    // segment e, edge n…2n-2 is right segment 2n-2-e, the other two are the end
    // caps (no figure). Stretching the centre segment slides everything after it
    // along that segment, which keeps every corner angle — so the face segment
    // changes by exactly the same amount.
    const n = pts.length;
    const band = wallBand(pts, wall.thickness, wall.align);
    const faceLen = (e: number): number => distance(band[e], band[(e + 1) % band.length]);
    const seg = (e: number): number => (e < n - 1 ? e : e >= n && e <= 2 * n - 2 ? 2 * n - 2 - e : -1);
    return {
      ring: band,
      ellipse: false,
      closed: true,
      length: faceLen,
      apply: (e, l) => {
        const s = seg(e);
        if (s < 0) return;
        if (resizeWallEdge(wall, s, s + 1, distance(pts[s], pts[s + 1]) + (l - faceLen(e)))) { pinAfter(s); done(); }
      },
      // Both faces of a segment show its one pin: it holds the segment's run.
      isLocked: (e) => wallSegPin(wall, seg(e)) != null,
      toggleLock: (e, on) => togglePin(seg(e), on),
    };
  }
  // A revision cloud (closed freeform ring): typing an edge length moves that
  // edge's end vertex along the edge, resizing the marked region.
  const cloud = state.selectedRevCloudId ? (state.drawing.revisionClouds || []).find((c) => c.id === state.selectedRevCloudId) : null;
  if (cloud && cloud.points.length >= 3) {
    const pts = cloud.points;
    return {
      ring: pts,
      ellipse: false,
      length: (e) => distance(pts[e], pts[(e + 1) % pts.length]),
      apply: (e, l) => {
        const a = pts[e], b = pts[(e + 1) % pts.length];
        const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1;
        if (l < 1 || Math.abs(l - L) < 1e-6) return;
        pushHistory();
        pts[(e + 1) % pts.length] = { x: a.x + (dx / L) * l, y: a.y + (dy / L) * l };
        renderProperties(); invalidate();
      },
    };
  }
  const inst = selectedInstance();
  if (inst && isPolyRoom(inst) && inst.polygon) {
    // One figure per WALL (corner to corner): a pane's jambs split the wall into
    // segments, but its length is still measured, typed and pinned as one wall.
    const view = wallView(inst);
    return {
      ring: view.polygon!.map((p) => instanceToWorld(inst, p)),
      ellipse: false,
      bothSides: true, // interior clear-span dimensions are editable too
      // The mitred inner ring, one point per wall corner — the ring the interior
      // dimensions are drawn on (roomInnerWorld), so the two index alike.
      innerRing: polyEdges(view).map((e) => instanceToWorld(inst, e.ia)),
      length: (e) => wallRuns(inst)[e]?.len ?? 0,
      // Resizing one wall can't drive its own locked figures (a pinned run / pane,
      // or a door) below their length: floor the typed length at the wall's min.
      apply: (e, l) => { l = Math.max(l, wallSpansMinLen(inst, e)); if (resizePolyWall(inst, e, l)) { setWallLengthPin(inst, e, Math.round(l)); regenerateSauna(inst); renderProperties(); invalidate(); } },
      isLocked: (e) => wallLengthPin(inst, e) != null,
      toggleLock: (e, on) => {
        pushHistory();
        setWallLengthPin(inst, e, on ? Math.round(wallRuns(inst)[e]?.len ?? 0) : null);
        regenerateSauna(inst); renderProperties(); invalidate();
      },
    };
  }
  // Node objects (salt wall / heated bench): their dimensions run along the node
  // polyline. Typing a segment length stretches that segment (moving the tail),
  // then the bench regenerates around the new node line.
  if (inst && inst.nodes && inst.nodes.length >= 2) {
    const nodes = inst.nodes;
    return {
      ring: nodes,
      ellipse: false,
      closed: false,
      length: (e) => distance(nodes[e], nodes[e + 1]),
      apply: (e, l) => {
        if (e + 1 >= nodes.length || l < 1 || Math.abs(l - distance(nodes[e], nodes[e + 1])) < 1e-6) return;
        pushHistory();
        resizeOpenPolylineSegment(inst.nodes!, e, e + 1, l);
        inst.x = inst.nodes!.reduce((s, p) => s + p.x, 0) / inst.nodes!.length;
        inst.y = inst.nodes!.reduce((s, p) => s + p.y, 0) / inst.nodes!.length;
        inst.width = Math.round(polyBenchLength(inst.nodes!));
        renderProperties(); invalidate();
      },
    };
  }
  // Any rectangular-footprint object (rooms + generic products): edges 0/2 are
  // the width, 1/3 the depth.
  if (inst && !(inst.nodes && inst.nodes.length)) {
    const t = wallThicknessOf(inst);
    const ihw = inst.width / 2 - t, ihd = inst.depth / 2 - t;
    const innerRing = inst.autoBenches && ihw > 5 && ihd > 5 ? [
      instanceToWorld(inst, { x: -ihw, y: -ihd }), instanceToWorld(inst, { x: ihw, y: -ihd }),
      instanceToWorld(inst, { x: ihw, y: ihd }), instanceToWorld(inst, { x: -ihw, y: ihd }),
    ] : undefined;
    return {
      ring: instanceCorners(inst),
      ellipse: false,
      bothSides: !!inst.autoBenches, // rooms draw interior clear-span dimensions too
      innerRing,
      length: (e) => (e % 2 === 0 ? inst.width : inst.depth),
      apply: (e, l) => { pushHistory(); if (e % 2 === 0) resizeInstance(inst, l, inst.depth); else resizeInstance(inst, inst.width, l); regenerateSauna(inst); renderProperties(); invalidate(); },
    };
  }
  return null;
}

/**
 * Which interior sub-dimension of `edge` a world point is over, when that wall
 * hosts partitions, and its current value. `before` is the clear span from the
 * wall's start corner to the partition's near face, `after` the span from its far
 * face to the end corner, `thickness` the partition itself. Null when the wall has
 * no partition (the whole interior span then edits the wall length as before).
 */
export function innerWallDimKind(
  inst: ProductInstance, edge: number, world: Vec2,
): { wallId: string; kind: 'before' | 'after' | 'thickness'; value: number } | null {
  const splits = innerWallDimSplits(inst, edge);
  if (!splits.length) return null;
  const m = polyInnerWallMetrics(inst, edge, toInstanceLocal(inst, world));
  const mit = innerFaceMitres(inst, edge);
  if (!m || !mit) return null;
  for (let i = 0; i < splits.length; i++) {
    const sp = splits[i];
    if (m.along < sp.s) {
      // The span before the FIRST partition runs from the mitred corner; a later
      // one runs from the previous partition's far face, so it positions that one.
      if (i === 0) return { wallId: sp.wallId, kind: 'before', value: sp.s - mit.m0 };
      return { wallId: splits[i - 1].wallId, kind: 'after', value: sp.s - splits[i - 1].t };
    }
    if (m.along <= sp.t) return { wallId: sp.wallId, kind: 'thickness', value: sp.t - sp.s };
  }
  const last = splits[splits.length - 1];
  return { wallId: last.wallId, kind: 'after', value: mit.innerLen + mit.m0 - last.t };
}

/** Apply a typed interior sub-dimension: slide the partition along its host wall
 *  so the clear span matches, or set its thickness. It stays on its own wall. */
function applyInnerWallDim(instId: string, wallId: string, kind: 'before' | 'after' | 'thickness', val: number): void {
  const inst = state.drawing.instances.find((i) => i.id === instId);
  const iw = inst ? innerWallsOf(inst).find((w) => w.id === wallId) : null;
  if (!inst || !iw || iw.edge == null) return;
  const e = polyEdges(wallView(inst))[iw.edge];
  const mit = innerFaceMitres(inst, iw.edge);
  if (!e || !mit) return;
  pushHistory();
  const faces = partitionFaces(inst);
  if (kind === 'thickness') {
    iw.thickness = Math.max(1, Math.round(val));
  } else {
    // The typed figure is a clear span on the inner face; `m0` converts it back to
    // a distance along the edge, then the partition is centred on that face.
    const h = innerWallThickness(inst, iw) / 2;
    const face = kind === 'before' ? val + mit.m0 : mit.innerLen + mit.m0 - val;
    const lo = h, hi = Math.max(h, e.len - h);
    iw.pos = Math.max(lo, Math.min(hi, kind === 'before' ? face + h : face - h));
  }
  benchesFollowPartitions(inst, faces);
  regenerateSauna(inst);
  renderProperties();
  invalidate();
}

/**
 * The joint of a wall's dimension chain under a screen point, on the SELECTED
 * room: where a glazed pane (or a door) meets the solid run beside it. The target
 * is the short line across the wall at that station — outer face to inner face —
 * with a 3 px tolerance, which is why it can sit inside the band that selects the
 * room without swallowing it.
 */
export function wallSpanJointAt(sp: Vec2): { inst: ProductInstance; edge: number; index: number } | null {
  const inst = selectedInstance();
  if (!inst || instLocked(inst) || !isPolyRoom(inst) || state.tool !== 'select') return null;
  const world = vp.screenToWorld(sp);
  const t = wallThicknessOf(inst);
  const edges = polyEdges(wallView(inst)); // one per WALL: the chain runs across jambs
  let best: { inst: ProductInstance; edge: number; index: number } | null = null;
  let bestD = 3 / vp.scale;
  for (let ei = 0; ei < edges.length; ei++) {
    const e = edges[ei];
    const spans = wallChain(inst, ei);
    if (spans.length <= 1) continue;
    for (const j of wallSpanJoints(spans)) {
      const p0 = { x: e.a.x + e.dir.x * j.at, y: e.a.y + e.dir.y * j.at };
      const p1 = { x: p0.x + e.normal.x * t, y: p0.y + e.normal.y * t };
      const d = distToSegment(world, instanceToWorld(inst, p0), instanceToWorld(inst, p1));
      if (d < bestD) { bestD = d; best = { inst, edge: ei, index: j.index }; }
    }
  }
  return best;
}

/** The chain joint a jamb vertex IS: its wall and the joint's index in that wall's
 *  chain (the figure on its left), or null. */
export function jambJoint(inst: ProductInstance, v: number): { wall: number; index: number } | null {
  const at = segToWall(inst, v); // segment v starts at jamb v
  if (!at) return null;
  const j = wallSpanJoints(wallChain(inst, at.wall)).find((x) => Math.abs(x.at - at.offset) < 1);
  return j ? { wall: at.wall, index: j.index } : null;
}

/** Drag that joint to `world`: the two figures either side are resized, both far
 *  ends stay put and the wall's total length never changes. */
export function dragWallSpanJoint(drag: { instId: string; edge: number; index: number }, world: Vec2): void {
  const inst = state.drawing.instances.find((i) => i.id === drag.instId);
  if (!inst || !isPolyRoom(inst)) return;
  const e = polyEdges(wallView(inst))[drag.edge];
  if (!e) return;
  const local = toInstanceLocal(inst, world);
  const along = (local.x - e.a.x) * e.dir.x + (local.y - e.a.y) * e.dir.y;
  const spans = wallChain(inst, drag.edge);
  if (!spans[drag.index] || !spans[drag.index + 1]) return;
  followVertices(state.drawing, inst.id, () => writeWallChain(inst, drag.edge, spans, moveWallSpanJoint(spans, drag.index, along)));
  regenerateSauna(inst);
  invalidate();
}

/** Which figure of a wall's outer dimension chain a world point is over, and its
 *  current value. Null when the wall hosts no door or pane — the whole edge then
 *  edits the wall length, exactly as before. */
export function wallDimKindAt(inst: ProductInstance, edge: number, world: Vec2): { index: number; value: number } | null {
  const spans = wallChain(inst, edge);
  if (spans.length <= 1) return null;
  const e = polyEdges(wallView(inst))[edge];
  if (!e) return null;
  const local = toInstanceLocal(inst, world);
  const along = (local.x - e.a.x) * e.dir.x + (local.y - e.a.y) * e.dir.y;
  for (let i = 0; i < spans.length; i++) {
    if (along <= spans[i].t || i === spans.length - 1) return { index: i, value: spans[i].t - spans[i].s };
  }
  return null;
}

/**
 * Apply a typed wall figure. The typed span is held at that length and the wall's
 * other figures absorb the difference — solid runs first, which is what makes
 * typing a solid run SLIDE the panes and doors along the wall while typing a
 * pane's own figure RESIZES it. The typed figure is then pinned.
 */
function applyWallDim(instId: string, edge: number, index: number, val: number): void {
  const inst = state.drawing.instances.find((i) => i.id === instId);
  if (!inst || !isPolyRoom(inst)) return;
  const e = polyEdges(wallView(inst))[edge];
  const spans = wallChain(inst, edge);
  const sp = spans[index];
  if (!e || !sp) return;
  const min = sp.kind === 'door' ? DOOR_MIN : sp.kind === 'glass' || sp.kind === 'opening' ? GLASS_MIN : 0;
  pushHistory();
  const want = Math.max(min, Math.min(e.len, val));
  // The whole WALL re-solves (its panes each own a segment, so the result is laid
  // back down as segments), and an equal group carries the change to other walls.
  followVertices(state.drawing, inst.id, () => solveWall(inst, edge, index, want));
  lockWallSpan(sp);
  regenerateSauna(inst);
  renderProperties();
  invalidate();
}

/** Right-clicking a PINNED wall figure offers to release it. Returns false (and
 *  shows nothing) anywhere else, so the rest of the context-menu chain runs. */
export function showWallDimMenu(e: MouseEvent): boolean {
  const inst = selectedInstance();
  const fdt = faceDimTarget();
  const world = dimLabelProbeAt(planHits, screenPos(e));
  if (!inst || !isPolyRoom(inst) || !fdt || !world) return false;
  const fe = faceDimEdgeAt(fdt, screenPos(e), world);
  if (fe.edge < 0 || fe.inside) return false;
  // The wall's overall figure is not a chain span — there is nothing to unpin on
  // it (a locked edge length is released from the editor's lock toggle).
  if (dimLabelTagAt(planHits, screenPos(e)) === DIM_TAG_WALL_OVERALL) return false;
  const hit = wallDimKindAt(inst, fe.edge, world);
  const span = hit ? wallChain(inst, fe.edge)[hit.index] : null;
  if (!span?.locked) return false;
  const menu = $('ctx-menu');
  menu.innerHTML = '';
  const b = document.createElement('button');
  b.textContent = tr('🔓 Unpin dimension');
  b.addEventListener('click', () => {
    hideCtxMenu();
    pushHistory();
    unlockWallSpan(span);
    renderProperties();
    invalidate();
  });
  menu.appendChild(b);
  menu.style.left = `${Math.min(e.clientX, window.innerWidth - 190)}px`;
  menu.style.top = `${Math.min(e.clientY, window.innerHeight - 80)}px`;
  menu.classList.remove('hidden');
  return true;
}

/** Wall-thickness the interior clear dimension removes on edge `e`: the outer
 *  edge length minus the corresponding inner-ring edge length. Used to show the
 *  interior value when editing an inside dimension and add the walls back on save. */
function faceDimWallInset(target: FaceDimTarget, e: number): number {
  const ir = target.innerRing;
  if (!ir || ir.length < 3) return 0;
  const innerLen = distance(ir[e], ir[(e + 1) % ir.length]);
  return Math.max(0, target.length(e) - innerLen);
}

/** Show the value editor over a dimension line, pre-filled with its length. */
export function openDimEditor(dimId: string, sp: Vec2): void {
  const d = state.drawing.dimensions.find((x) => x.id === dimId);
  if (!d) return;
  openFigureEditor({ kind: 'dimension', dimId }, String(Math.round(distance(d.a, d.b))), sp);
}
/** Show the value editor over one figure of a wall's outer dimension chain — a
 *  solid run, or the opening a door or glazed pane cuts in it. */
export function openWallDimEditor(inst: ProductInstance, edge: number, hit: { index: number; value: number }, sp: Vec2): void {
  openFigureEditor({ kind: 'wall', instId: inst.id, edge, index: hit.index }, String(Math.round(hit.value)), sp);
}
/** Show the value editor over a selected shape's temporary face dimension. An
 *  inside (interior clear-span) dimension shows the interior value — the outer
 *  edge minus the wall thickness at each end — and commits it back with the walls. */
/** Set a corner's rounding radius from its `R<n>` dimension, clamped the same way
 *  the properties panel clamps it. Typing 0 squares the corner off again. */
function applyCornerRadius(instId: string, vertex: number, value: number): void {
  const inst = state.drawing.instances.find((i) => i.id === instId);
  if (!inst || !inst.polygon || vertex >= inst.polygon.length) return;
  pushHistory();
  const arr = (inst.polygonRadii ?? []).slice();
  while (arr.length < inst.polygon.length) arr.push(null);
  arr[vertex] = Math.max(0, Math.min(Math.round(value), Math.round(maxCornerRadius(inst, vertex))));
  inst.polygonRadii = arr.some((r) => (r ?? 0) > 0) ? arr : undefined;
  regenerateSauna(inst);
  renderProperties();
  invalidate();
}

/** The rounded corner whose radius leader is under `sp` (screen), or null. */
export function cornerRadiusDimAt(sp: Vec2): { inst: ProductInstance; vertex: number } | null {
  const inst = selectedInstance() ?? polyRoomEditInst();
  if (!inst || !isPolyRoom(inst)) return null;
  for (const c of roomCorners(inst)) {
    if (!c) continue;
    const { on, far } = cornerRadiusLeader(inst, c);
    const a = vp.worldToScreen(on), b = vp.worldToScreen(far);
    // The label sits just past the far end, so allow a little slack there.
    const d = distToSegment(sp, a, { x: b.x + (b.x >= a.x ? 34 : -34), y: b.y });
    if (d <= 9) return { inst, vertex: c.v };
  }
  return null;
}

export function openFaceDimEditor(target: FaceDimTarget, edge: number, sp: Vec2, inside = false): void {
  const shown = inside ? target.length(edge) - faceDimWallInset(target, edge) : target.length(edge);
  openFigureEditor({ kind: 'face', target, edge, inside }, String(Math.round(shown)), sp);
}
const dimInput = (): HTMLInputElement => $('dim-input') as HTMLInputElement;
const dimLockBtn = (): HTMLButtonElement => $('dim-lock') as HTMLButtonElement;
let figures: InlineEditor | null = null;
/** The figure editor: the one inline editor's life cycle over `#dim-input`. */
const figureEditor = (): InlineEditor => (figures ??= inlineEditor(dimInput()));
/** The figure being retyped, while the editor is open. */
let editing: DimEdit | null = null;

/** Open the value editor at `sp` (screen) on a figure, showing `text`. Values are
 *  in mm (matching the panel's "Length (mm)" fields); a unit suffix or arithmetic
 *  is accepted too (`parseMeasure`). Enter or a click away keeps a CHANGED value,
 *  Escape puts it back: committing the value it opened with does nothing — no
 *  resize, no step, and above all no pin (a wall figure's apply pins it), so
 *  opening a figure to read it and clicking away leaves it as it was. */
export function openFigureEditor(edit: DimEdit, text: string, sp: Vec2): void {
  const opened = parseMeasure(text, 'mm');
  const ed = figureEditor();
  ed.open({
    text,
    look: () => ({ left: `${sp.x}px`, top: `${sp.y}px` }),
    keys: { enter: 'commits', esc: 'cancels' },
    gesture: false, // each figure's apply is its own undo step
    caret: 'all',
    commit: (typed) => {
      const val = parseMeasure(typed, 'mm') ?? NaN;
      if (Number.isFinite(val) && opened != null && Math.round(val) === Math.round(opened)) return;
      applyFigure(edit, val);
    },
    closed: () => {
      if (editing === edit) editing = null;
      dimLockBtn().classList.add('hidden');
      invalidate();
    },
  });
  editing = edit;
  renderDimLock(sp); // the lock toggle sits just to its right
}

/** A typed figure applied to what it measures. Each kind takes the values it can:
 *  a corner radius 0 (squared off again; nothing typed counts as 0), a constraint
 *  and a spot's distance 0 and up, every length more than 0. */
function applyFigure(e: DimEdit, val: number): void {
  switch (e.kind) {
    case 'corner': applyCornerRadius(e.instId, e.vertex, Number.isFinite(val) ? val : 0); return;
    case 'constraint': { const c = objConstraints().find((x) => x.id === e.id); if (c && val >= 0) setConstraintValue(c, val); return; }
    case 'spot': if (val >= 0) applySpotFigure(e.edit, val); return;
    case 'support': if (val > 0) applySupportFigure(e.edit, val); return;
    case 'innerWall': if (val > 0) applyInnerWallDim(e.instId, e.wallId, e.part, val); return;
    case 'dimension': if (val > 0) applyDimensionValue(e.dimId, val); return;
    case 'wall': if (val > 0) applyWallDim(e.instId, e.edge, e.index, val); return;
    // A temporary face dimension resizes that edge (respecting any locks / real
    // dimensions); it is never stored, so nothing else references it. An interior
    // dimension is entered as the clear span, so add the walls back before resizing.
    case 'face': if (val > 0) e.target.apply(e.edge, e.inside ? val + faceDimWallInset(e.target, e.edge) : val); return;
    default: unreachable(e, 'figure');
  }
}

/** The lock a figure carries — a room wall's chain figure, or a face edge whose
 *  length can be pinned — or null where there is none (the toggle hides). */
function figureLock(e: DimEdit): { locked: boolean; toggle: () => void } | null {
  switch (e.kind) {
    case 'wall': {
      const inst = state.drawing.instances.find((i) => i.id === e.instId);
      if (!inst || !isPolyRoom(inst)) return null;
      const span = wallChain(inst, e.edge)[e.index];
      if (!span) return null;
      return {
        locked: !!span.locked,
        toggle: () => {
          pushHistory();
          if (span.locked) unlockWallSpan(span); else lockWallSpan(span);
          regenerateSauna(inst); renderProperties(); invalidate();
        },
      };
    }
    case 'face': {
      const { target, edge } = e;
      if (!target.isLocked || !target.toggleLock) return null;
      const locked = target.isLocked(edge);
      return { locked, toggle: () => target.toggleLock!(edge, !locked) };
    }
    case 'constraint':
    case 'corner':
    case 'dimension':
    case 'innerWall':
    case 'spot':
    case 'support':
      return null;
    default: return unreachable(e, 'figure');
  }
}

/** Position (when `sp` is given), show and style the lock toggle for the figure
 *  being retyped. Hidden when it carries no lock. */
function renderDimLock(sp?: Vec2): void {
  const btn = dimLockBtn();
  const lock = editing ? figureLock(editing) : null;
  if (!lock) { btn.classList.add('hidden'); return; }
  if (sp) { btn.style.left = `${sp.x + 60}px`; btn.style.top = `${sp.y}px`; }
  btn.classList.remove('hidden');
  btn.classList.toggle('locked', lock.locked);
  btn.textContent = lock.locked ? '🔒' : '🔓';
  btn.title = tr(lock.locked ? 'Unlock this dimension' : 'Lock this dimension');
}

/** The figure editor's lock button and its hover title (its keys, blur and focus
 *  are the one inline editor's). */
export function wireDimEditor(): void {
  figureEditor();
  dimInput().title = tr('Length in mm — you can also type cm / m or an expression');
  // The lock toggle must NOT blur the input (a blur commits and closes the editor),
  // so it acts on mousedown with the default focus shift prevented; the editor
  // stays open over the same figure.
  dimLockBtn().addEventListener('mousedown', (e) => {
    e.preventDefault();
    if (editing) figureLock(editing)?.toggle();
    renderDimLock();
  });
}

/** Local-frame endpoints of a glass pane, along the wall it sits on. */
function glassEndpoints(inst: ProductInstance, g: GlassSegment): [Vec2, Vec2] {
  const hw = inst.width / 2, hd = inst.depth / 2;
  if (g.wall === 'front' || g.wall === 'back') {
    const y = g.wall === 'front' ? -hd : hd;
    return [{ x: g.start, y }, { x: g.end, y }];
  }
  const x = g.wall === 'left' ? -hw : hw;
  return [{ x, y: g.start }, { x, y: g.end }];
}

/** Whether a local point is a corner of the room rectangle (outer or inner wall). */
function isRoomCorner(inst: ProductInstance, p: Vec2): boolean {
  const hw = inst.width / 2, hd = inst.depth / 2, wt = wallThicknessOf(inst);
  const TOL = 4;
  const onX = Math.abs(Math.abs(p.x) - hw) < TOL || Math.abs(Math.abs(p.x) - (hw - wt)) < TOL;
  const onY = Math.abs(Math.abs(p.y) - hd) < TOL || Math.abs(Math.abs(p.y) - (hd - wt)) < TOL;
  return onX && onY;
}

/**
 * If the dimension (its two local endpoints) measures a sauna feature — a glass
 * pane, the door, or a wall dent — resize ONLY that feature to `newLen` and
 * return the feature's new local endpoints. Otherwise return null, so the caller
 * can fall back to resizing the whole room.
 */
function tryResizeDimFeature(inst: ProductInstance, pa: Vec2, pb: Vec2, newLen: number): [Vec2, Vec2] | null {
  if (!inst.autoBenches) return null;
  const TOL = 6; // mm — anchors are re-snapped to exact feature points
  const at = (p: Vec2, t: Vec2): boolean => Math.abs(p.x - t.x) < TOL && Math.abs(p.y - t.y) < TOL;
  const pair = (a: Vec2, b: Vec2): boolean => (at(pa, a) && at(pb, b)) || (at(pa, b) && at(pb, a));

  // Door: the two jambs → change the door width, keeping its centre.
  const rectDoor = mainRoomDoor(inst);
  const dp = rectDoor ? doorParts(inst, rectDoor) : null;
  if (dp && rectDoor && pair(dp.jambL, dp.jambR)) {
    pushHistory();
    rectDoor.width = Math.max(300, newLen);
    clampDoor(inst, rectDoor);
    regenerateSauna(inst);
    const np = doorParts(inst, rectDoor);
    return [np.jambL, np.jambR];
  }

  // Glass: a pane's two ends → change its length, keeping its centre.
  for (const g of inst.glass || []) {
    const [a, b] = glassEndpoints(inst, g);
    if (pair(a, b)) {
      pushHistory();
      const centre = (g.start + g.end) / 2;
      const s = clampToWall(inst, g.wall, centre - newLen / 2);
      const e = clampToWall(inst, g.wall, centre + newLen / 2);
      g.start = Math.min(s, e);
      g.end = Math.max(s, e);
      clampGlassToDoor(inst);
      regenerateSauna(inst);
      return glassEndpoints(inst, g);
    }
  }

  // Opening: a span's two ends (outer OR inner wall face) → change its length,
  // keeping its centre.
  const wt = wallThicknessOf(inst);
  for (const g of inst.openings || []) {
    const [a, b] = glassEndpoints(inst, g); // ends at the outer wall line
    const inset = (p: Vec2): Vec2 =>
      g.wall === 'front' ? { x: p.x, y: p.y + wt } : g.wall === 'back' ? { x: p.x, y: p.y - wt }
        : g.wall === 'left' ? { x: p.x + wt, y: p.y } : { x: p.x - wt, y: p.y };
    if (pair(a, b) || pair(inset(a), inset(b))) {
      pushHistory();
      const centre = (g.start + g.end) / 2;
      const s = clampToWall(inst, g.wall, centre - newLen / 2);
      const e = clampToWall(inst, g.wall, centre + newLen / 2);
      g.start = Math.min(s, e);
      g.end = Math.max(s, e);
      return glassEndpoints(inst, g);
    }
  }

  return null;
}

/**
 * If exactly one dimension endpoint sits on a movable feature (glass, opening or
 * the door) and the other is a fixed point (e.g. a room corner), MOVE that
 * feature along its wall so the straight-line distance to the fixed point becomes
 * `newLen`, keeping the perpendicular offset fixed. Returns the dimension's new
 * local endpoints in (pa, pb) order, or null when no feature endpoint matches.
 */
function tryMoveFeatureToDim(inst: ProductInstance, pa: Vec2, pb: Vec2, newLen: number): [Vec2, Vec2] | null {
  if (!inst.autoBenches) return null;
  const TOL = 6;
  const at = (p: Vec2, t: Vec2): boolean => Math.abs(p.x - t.x) < TOL && Math.abs(p.y - t.y) < TOL;
  const hw = inst.width / 2, hd = inst.depth / 2, wt = wallThicknessOf(inst);

  // The along-axis shift that makes |F → moved E| == newLen (perp kept fixed).
  const solveDelta = (E: Vec2, F: Vec2, horiz: boolean): number | null => {
    const Ec = horiz ? E.x : E.y, Fc = horiz ? F.x : F.y;
    const perp = horiz ? Math.abs(E.y - F.y) : Math.abs(E.x - F.x);
    if (newLen <= perp + 1e-6) return null; // unreachable for this perpendicular gap
    const alongNew = Math.sign(Ec - Fc || 1) * Math.sqrt(newLen * newLen - perp * perp);
    return Fc + alongNew - Ec;
  };
  // Match a feature endpoint to one of the dimension ends, then move it.
  const tryFeature = (
    edges: Vec2[], horiz: boolean, clampDelta: (d: number) => number, apply: (d: number) => void
  ): [Vec2, Vec2] | null => {
    for (const cand of edges) {
      const onPa = at(pa, cand), onPb = !onPa && at(pb, cand);
      if (!onPa && !onPb) continue;
      const E = onPa ? pa : pb, F = onPa ? pb : pa;
      const raw = solveDelta(E, F, horiz);
      if (raw == null) return null;
      const delta = clampDelta(raw);
      if (Math.abs(delta) > 1e-6) { pushHistory(); apply(delta); regenerateSauna(inst); }
      const newE = horiz ? { x: E.x + delta, y: E.y } : { x: E.x, y: E.y + delta };
      return onPa ? [newE, F] : [F, newE];
    }
    return null;
  };

  // Glass / openings: shift the whole span along its wall, clamped inside it.
  const insetOf = (p: Vec2, wall: WallSide): Vec2 =>
    wall === 'front' ? { x: p.x, y: p.y + wt } : wall === 'back' ? { x: p.x, y: p.y - wt }
      : wall === 'left' ? { x: p.x + wt, y: p.y } : { x: p.x - wt, y: p.y };
  for (const isOpening of [false, true]) {
    for (const seg of (isOpening ? inst.openings : inst.glass) || []) {
      const horiz = seg.wall === 'front' || seg.wall === 'back';
      const [e1, e2] = glassEndpoints(inst, seg);
      const edges = isOpening ? [e1, e2, insetOf(e1, seg.wall), insetOf(e2, seg.wall)] : [e1, e2];
      const lo = horiz ? -hw : -hd, hi = horiz ? hw : hd;
      const sLo = Math.min(seg.start, seg.end), sHi = Math.max(seg.start, seg.end);
      const res = tryFeature(edges, horiz,
        (d) => Math.max(lo - sLo, Math.min(hi - sHi, d)),
        (d) => { seg.start += d; seg.end += d; if (!isOpening) clampGlassToDoor(inst); });
      if (res) return res;
    }
  }

  // Door: shift its centre offset along the front wall.
  const rectDoor = mainRoomDoor(inst);
  const dp = rectDoor ? doorParts(inst, rectDoor) : null;
  if (dp && rectDoor) {
    const halfW = rectDoor.width / 2;
    const res = tryFeature([dp.jambL, dp.jambR], true,
      (d) => Math.max(-hw + halfW, Math.min(hw - halfW, rectDoor.offset + d)) - rectDoor.offset,
      (d) => { rectDoor.offset += d; clampDoor(inst, rectDoor); });
    if (res) return res;
  }
  return null;
}

/** Re-point a dimension endpoint to a local point on its instance (anchored). */
function setDimEndpoint(inst: ProductInstance, d: DimensionLine, which: 'a' | 'b', local: Vec2): void {
  const hw = inst.width / 2, hd = inst.depth / 2;
  const ref: DimAnchor = { instId: inst.id, nx: hw ? local.x / hw : 0, ny: hd ? local.y / hd : 0, snapped: true };
  // If the point lands on a tagged feature edge, keep that tag so the dimension
  // keeps tracking the feature (not just a proportional room position).
  const w = instanceToWorld(inst, local);
  for (const cand of instanceSnapPoints(inst)) {
    if (cand.anchor?.feat && distance(cand.p, w) < 2) { ref.feat = cand.anchor.feat; break; }
  }
  if (which === 'a') { d.a = w; d.aRef = ref; }
  else { d.b = w; d.bRef = ref; }
}

/**
 * A dimension whose two ends both anchor to corners of one ring shape (a drawn
 * polygon or a polygonal room) — the bridge that lets a user dimension act as a
 * GCS distance constraint on that ring. Carries the shape and the two corner
 * indices the dimension spans.
 */
type DimRingRef =
  | { kind: 'poly'; poly: Polygon; ai: number; bi: number }
  | { kind: 'room'; inst: ProductInstance; ai: number; bi: number };
export function dimRingRef(d: DimensionLine): DimRingRef | null {
  if (d.aRef?.shape && d.bRef?.shape && d.aRef.shape.id === d.bRef.shape.id) {
    const poly = (state.drawing.polygons || []).find((p) => p.id === d.aRef!.shape!.id);
    if (poly && d.aRef.shape.vertex !== d.bRef.shape.vertex) return { kind: 'poly', poly, ai: d.aRef.shape.vertex, bi: d.bRef.shape.vertex };
  }
  if (d.aRef?.feat?.kind === 'polyv' && d.bRef?.feat?.kind === 'polyv' && d.aRef.instId === d.bRef.instId && d.aRef.feat.vertex !== d.bRef.feat.vertex) {
    const inst = state.drawing.instances.find((i) => i.id === d.aRef!.instId);
    if (inst?.polygon && isPolyRoom(inst)) return { kind: 'room', inst, ai: d.aRef.feat.vertex, bi: d.bRef.feat.vertex };
  }
  return null;
}
export function ringRefPts(r: DimRingRef): Vec2[] { return r.kind === 'poly' ? r.poly.points : r.inst.polygon!; }
export function ringRefCons(r: DimRingRef): RingCons { return r.kind === 'poly' ? polyRingCons(r.poly) : roomRingCons(r.inst); }
export function ringRefRelations(r: DimRingRef): ShapeRelation[] | undefined { return r.kind === 'poly' ? r.poly.relations : r.inst.polygonRelations; }
export function addRingRelation(r: DimRingRef, rel: ShapeRelation): void { if (r.kind === 'poly') addPolyRelation(r.poly, rel); else addRoomRelation(r.inst, rel); }
export function removeRingRelation(r: DimRingRef, index: number): void { if (r.kind === 'poly') removePolyRelation(r.poly, index); else removeRoomRelation(r.inst, index); }

/** Index of a persistent distance constraint between corners ai,bi (either order), or -1. */
export function findDistanceRelation(rels: ShapeRelation[] | undefined, ai: number, bi: number): number {
  return (rels ?? []).findIndex((r) => r.type === 'distancePP'
    && ((r.verts[0] === ai && r.verts[1] === bi) || (r.verts[0] === bi && r.verts[1] === ai)));
}

/** Write a solved ring back to its shape (poly-room refreshes its box + parts). */
function applyRingSolve(r: DimRingRef, next: Vec2[]): void {
  if (r.kind === 'poly') { r.poly.points = next; return; }
  r.inst.polygon = next;
  recenterPolyRoom(r.inst); // keep the bounding box centred on the reshaped room
  regenerateSauna(r.inst);
}

/** Change a persistent distance constraint's value in place and re-solve the ring. */
function setDistanceRelationValue(r: DimRingRef, index: number, value: number): void {
  const rels = ringRefRelations(r);
  const rel = rels?.[index];
  if (!rel || rel.type !== 'distancePP') return;
  pushHistory();
  rel.value = value;
  const next = gcsSolveRing(ringRefPts(r), ringRefCons(r));
  if (next) applyRingSolve(r, next);
}

/**
 * Apply a typed length to a dimension. A dimension between two corners of one
 * ring shape acts as a GCS distance constraint: if that distance is locked it
 * drives the whole ring through the solver, else a non-adjacent span is solved
 * one-shot (when the ring carries locks). Otherwise a dimension snapped to a
 * sauna feature (glass, door, dent) resizes only that feature; one snapped to
 * the room's extremities resizes the whole room; anything else stretches the
 * dimension itself along its own direction.
 */
function applyDimensionValue(dimId: string, newLen: number): void {
  const d = state.drawing.dimensions.find((x) => x.id === dimId);
  if (!d) return;
  const curLen = distance(d.a, d.b);
  if (curLen < 1e-6 || Math.abs(newLen - curLen) < 1e-6) return;

  // A dimension across a ring shape's corners drives it through the solver: a
  // locked distance updates its constraint; an unlocked non-adjacent span (a
  // diagonal) resizes one-shot when the ring is otherwise constrained. Adjacent
  // spans fall through to the edge-resize path below (which also handles free
  // rings via the single-vertex heuristic).
  const rr = dimRingRef(d);
  if (rr) {
    const relIdx = findDistanceRelation(ringRefRelations(rr), rr.ai, rr.bi);
    if (relIdx >= 0) {
      setDistanceRelationValue(rr, relIdx, newLen);
      syncDimensions(); renderProperties(); invalidate();
      return;
    }
    const n = ringRefPts(rr).length;
    const adjacent = (rr.ai + 1) % n === rr.bi || (rr.bi + 1) % n === rr.ai;
    if (!adjacent && ringHasLocks(ringRefCons(rr))) {
      const next = gcsDistanceResize(ringRefPts(rr), ringRefCons(rr), rr.ai, rr.bi, newLen);
      if (next) {
        pushHistory();
        applyRingSolve(rr, next);
        syncDimensions(); renderProperties(); invalidate();
        return;
      }
    }
  }

  // Drawn-wall/line edge: a dimension between two adjacent centre-line vertices
  // resizes that segment.
  if (d.aRef?.wall && d.bRef?.wall && d.aRef.wall.id === d.bRef.wall.id) {
    const wall = (state.drawing.walls || []).find((w) => w.id === d.aRef!.wall!.id);
    if (wall && resizeWallEdge(wall, d.aRef.wall.vertex, d.bRef.wall.vertex, newLen)) {
      syncDimensions();
      renderProperties();
      invalidate();
      return;
    }
  }

  // Drawn-polygon edge: a dimension between two adjacent vertices resizes that
  // segment by moving one of them.
  if (d.aRef?.shape && d.bRef?.shape && d.aRef.shape.id === d.bRef.shape.id) {
    const poly = (state.drawing.polygons || []).find((p) => p.id === d.aRef!.shape!.id);
    if (poly && resizePolyShapeEdge(poly, d.aRef.shape.vertex, d.bRef.shape.vertex, newLen, d.id)) {
      syncDimensions();
      renderProperties();
      invalidate();
      return;
    }
  }

  if (d.aRef && d.bRef && d.aRef.instId === d.bRef.instId) {
    const inst = state.drawing.instances.find((i) => i.id === d.aRef!.instId);
    if (inst) {
      // Polygonal room: a dimension between two adjacent drawn corners resizes
      // that wall segment by moving one of its vertices.
      const fa = d.aRef.feat, fb = d.bRef.feat;
      if (isPolyRoom(inst) && fa?.kind === 'polyv' && fb?.kind === 'polyv') {
        if (resizePolyEdge(inst, fa.vertex, fb.vertex, newLen)) {
          syncDimensions();
          renderProperties();
          invalidate();
          return;
        }
      }
      const pa = toInstanceLocal(inst, d.a);
      const pb = toInstanceLocal(inst, d.b);
      // Feature dimension → resize only that feature, then re-point the anchors
      // to its new ends (preserving which endpoint was which).
      const ends = tryResizeDimFeature(inst, pa, pb, newLen);
      if (ends) {
        const [na, nb] = ends;
        const keep = distance(pa, na) + distance(pb, nb) <= distance(pa, nb) + distance(pb, na);
        setDimEndpoint(inst, d, 'a', keep ? na : nb);
        setDimEndpoint(inst, d, 'b', keep ? nb : na);
        syncDimensions();
        renderProperties();
        invalidate();
        return;
      }
      // Room dimension → resize the whole room, but only when both endpoints sit
      // on the room's extremities (outer/inner wall corners), not a feature.
      if (isRoomCorner(inst, pa) && isRoomCorner(inst, pb)) {
        const dnx = Math.abs(d.bRef.nx - d.aRef.nx);
        const dny = Math.abs(d.bRef.ny - d.aRef.ny);
        const alongX = dnx * inst.width >= dny * inst.depth;
        if (alongX && dnx > 1e-6) {
          pushHistory();
          resizeInstance(inst, (2 * newLen) / dnx, inst.depth);
          syncDimensions();
          renderProperties();
          invalidate();
          return;
        }
        if (!alongX && dny > 1e-6) {
          pushHistory();
          resizeInstance(inst, inst.width, (2 * newLen) / dny);
          syncDimensions();
          renderProperties();
          invalidate();
          return;
        }
      }
      // One end on a feature edge, the other fixed → move the feature so the
      // dimension reads `newLen`, then re-anchor both ends to their new points.
      const moved = tryMoveFeatureToDim(inst, pa, pb, newLen);
      if (moved) {
        setDimEndpoint(inst, d, 'a', moved[0]);
        setDimEndpoint(inst, d, 'b', moved[1]);
        syncDimensions();
        renderProperties();
        invalidate();
        return;
      }
    }
  }

  // A standalone annotation (neither end tracks an object): setting its length
  // just slides the far end along the dimension's own axis.
  if (!d.aRef && !d.bRef) {
    pushHistory();
    const ux = (d.b.x - d.a.x) / curLen;
    const uy = (d.b.y - d.a.y) / curLen;
    d.b = { x: d.a.x + ux * newLen, y: d.a.y + uy * newLen };
    renderProperties();
    invalidate();
    return;
  }
  // Anchored to object(s) but no edge/feature/room resize path applied (e.g. the
  // two ends sit on unrelated objects): a single length can't resize anything, so
  // leave the dimension reading the true distance instead of silently detaching
  // its endpoint and misreporting the geometry.
  flashStatus(tr('This dimension isn’t on a single resizable edge — value unchanged.'));
}
